import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { to: "/work", label: "Work" },
  { to: "/studio", label: "Studio" },
  { to: "/journal", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dark, setDark] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (location.pathname === "/") {
        setDark(y < window.innerHeight * 0.7);
      } else {
        setDark(false);
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const isDark = dark && !menuOpen;
  const textCls = isDark ? "text-paper" : "text-ink";
  const barBg = isDark ? "bg-transparent" : "bg-paper";
  const border = isDark ? "border-transparent" : "border-ink/10";

  // Only the typography wordmark — no symbol. Mist on dark hero, greige on paper.
  const wordmarkSrc = isDark ? "/brand/typography-mist.png" : "/brand/typography-greige.png";

  return (
    <>
      {/* Top scrim under dark-hero state — legibility for centred wordmark + nav */}
      {isDark && (
        <div
          aria-hidden
          className="fixed top-0 left-0 right-0 z-30 pointer-events-none"
          style={{
            height: 200,
            background: "linear-gradient(to bottom, rgba(14,17,19,0.55) 0%, rgba(14,17,19,0) 100%)",
          }}
        />
      )}

      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-40 border-b ${border} ${barBg}`}
        style={{ transition: "background-color 500ms cubic-bezier(0.16,1,0.3,1), border-color 500ms" }}
      >
        {/* Three-column bar: nav-left | centered wordmark | nav-right */}
        <div className="container-w grid grid-cols-3 items-center h-24 md:h-32">
          {/* Left nav */}
          <nav className={`hidden md:flex items-center gap-10 justify-self-start ${textCls}`} aria-label="Primary">
            <Link to="/work" data-testid="nav-work" className="mono-label link-underline pb-1" style={{ color: "inherit" }}>Work</Link>
            <Link to="/studio" data-testid="nav-studio" className="mono-label link-underline pb-1" style={{ color: "inherit" }}>Studio</Link>
          </nav>

          {/* Centre wordmark — big */}
          <div className="justify-self-center col-span-1 md:col-span-1 order-first md:order-none flex justify-center w-full">
            <Link to="/" data-testid="header-logo" className="block">
              <img
                src={wordmarkSrc}
                alt="TWP —"
                className="h-6 md:h-8 lg:h-10 w-auto"
              />
            </Link>
          </div>

          {/* Right nav */}
          <nav className={`hidden md:flex items-center gap-10 justify-self-end ${textCls}`} aria-label="Primary">
            <Link to="/contact" data-testid="nav-contact" className="mono-label link-underline pb-1" style={{ color: "inherit" }}>Contact</Link>
          </nav>

          {/* Mobile menu button — sits in right col */}
          <button
            className={`md:hidden mono-label justify-self-end ${textCls}`}
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-expanded={menuOpen}
          >
            {menuOpen ? "Close" : "Menu"}
          </button>
        </div>
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="md:hidden overflow-hidden border-t border-ink/10 bg-paper"
            >
              <div className="container-w py-8 flex flex-col gap-6">
                {NAV.map((n) => (
                  <Link key={n.to} to={n.to} className="font-display italic text-2xl text-ink link-underline w-fit" data-testid={`mnav-${n.label.toLowerCase()}`}>
                    {n.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
